import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Modal, Form, Input, Popconfirm, Card, Switch } from 'antd';
import { TrInputNumber as InputNumber } from '../components/TrInputNumber';
import { PlusOutlined, EditOutlined, DeleteOutlined, ApartmentOutlined } from '@ant-design/icons';
import {
  fetchExternalServiceItems,
  createExternalServiceItem,
  updateExternalServiceItem,
  deleteExternalServiceItem,
  type ExternalServiceItemDto
} from '../services/serviceOrderService';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useCanDelete } from '../hooks/useCanDelete';

const ExternalServiceItems: React.FC = () => {
  const isMobile = useBreakpoint();
  const canDelete = useCanDelete();
  const [items, setItems] = useState<ExternalServiceItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<ExternalServiceItemDto | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchExternalServiceItems();
      setItems(data);
    } catch (error) {
      message.error('Dış hizmet kalemleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({ defaultPrice: 0, isActive: true });
    setModalVisible(true);
  };

  const handleEdit = (item: ExternalServiceItemDto) => {
    setEditingItem(item);
    form.setFieldsValue({
      name: item.name,
      description: item.description,
      defaultPrice: item.defaultPrice,
      isActive: item.isActive
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (editingItem) {
        await updateExternalServiceItem(editingItem.id, {
          name: values.name,
          description: values.description,
          defaultPrice: values.defaultPrice ?? 0,
          isActive: values.isActive !== undefined ? values.isActive : true
        });
        message.success('Dış hizmet kalemi güncellendi');
      } else {
        await createExternalServiceItem({
          name: values.name,
          description: values.description,
          defaultPrice: values.defaultPrice ?? 0
        });
        message.success('Dış hizmet kalemi eklendi');
      }

      setModalVisible(false);
      form.resetFields();
      loadItems();
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('İşlem sırasında hata oluştu');
      }
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteExternalServiceItem(id);
      message.success('Dış hizmet kalemi silindi');
      loadItems();
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Dış hizmet kalemi silinirken hata oluştu');
      }
    }
  };

  const columns = [
    {
      title: 'Hizmet Adı',
      dataIndex: 'name',
      key: 'name'},
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-'},
    {
      title: 'Varsayılan Fiyat',
      dataIndex: 'defaultPrice',
      key: 'defaultPrice',
      render: (price: number) => `${price.toFixed(2)} ₺`},
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <span style={{ color: isActive ? 'green' : 'red' }}>
          {isActive ? 'Aktif' : 'Pasif'}
        </span>
      )},
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: ExternalServiceItemDto) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Düzenle
          </Button>
          {canDelete && (
          <Popconfirm
            title="Bu dış hizmet kalemini silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
              Sil
            </Button>
          </Popconfirm>
          )}
        </Space>
      )},
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>
          <ApartmentOutlined /> Dış Hizmet Kalemleri
        </h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Dış Hizmet Kalemi Ekle
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={loading}
          scroll={{ x: 500 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: !isMobile,
            showTotal: (total) => `Toplam ${total} dış hizmet kalemi`}}
        />
      </Card>

      <Modal
        title={editingItem ? 'Dış Hizmet Kalemini Düzenle' : 'Yeni Dış Hizmet Kalemi Ekle'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingItem(null);
        }}
        okText="Kaydet"
        cancelText="İptal"
        width={isMobile ? '100%' : 600}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Hizmet Adı"
            rules={[{ required: true, message: 'Lütfen hizmet adı girin' }]}
          >
            <Input placeholder="Örn: Kaporta, Cam, Oto Elektrik" />
          </Form.Item>
          <Form.Item name="description" label="Açıklama">
            <Input.TextArea rows={3} placeholder="Hizmet açıklaması" />
          </Form.Item>
          <Form.Item
            name="defaultPrice"
            label="Varsayılan Fiyat"
            rules={[{ required: true, message: 'Lütfen varsayılan fiyat girin' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.01}
              precision={2}
              placeholder="0,00"
            />
          </Form.Item>
          {editingItem && (
            <Form.Item name="isActive" label="Aktif" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default ExternalServiceItems;
